import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "United States",
  "United Kingdom",
  "Canada",
  "Jamaica",
  "The Bahamas",
  "Cayman Islands",
  "Trinidad and Tobago",
  "Barbados",
  "Turks and Caicos Islands",
  "Australia",
  "New Zealand",
  "Germany",
  "France",
  "Netherlands",
  "Spain",
  "Ireland",
  "United Arab Emirates",
  "Japan",
  "Other",
];

const inputClass =
  "w-full bg-transparent border-b border-navy/25 focus:border-gold-hover outline-none py-3 text-navy placeholder:text-muted-foreground/60 transition-colors duration-300";

const labelClass = "text-xs uppercase tracking-[0.2em] font-bold text-navy";

const ConsultationSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", country: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your request. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="consultation" className="bg-navy py-24 md:py-32 scroll-mt-24" data-testid="home-consultation-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <Reveal className="text-center">
          <Eyebrow light>Book a Consultation</Eyebrow>
          <h2 className="font-serif font-light text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight text-balance">
            Start the <em className="italic text-gold">conversation.</em>
          </h2>
          <p className="text-cream/70 max-w-xl mx-auto mt-6 text-base md:text-lg leading-relaxed">
            Tell us briefly about your legal matter and our team will review
            your details and follow up via email.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-14">
          {submitted ? (
            <div
              data-testid="consultation-success-message"
              className="bg-card border border-gold/40 p-10 md:p-14 text-center"
            >
              <CheckCircle2 size={40} className="text-gold-hover mx-auto" />
              <p className="font-serif text-navy text-2xl md:text-3xl mt-6 leading-snug max-w-2xl mx-auto">
                Thank you for your request. Our team will review your details
                and follow up via email within 24–48 hours.
              </p>
              <button
                data-testid="consultation-another-request-button"
                onClick={() => setSubmitted(false)}
                className="mt-8 text-sm font-bold uppercase tracking-[0.2em] text-navy border-b border-gold pb-1 transition-colors duration-300 hover:text-gold-hover"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="home-consultation-form"
              className="bg-card border border-navy/10 p-8 md:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label htmlFor="home-name" className={labelClass}>Name</label>
                  <input id="home-name" data-testid="home-form-name" type="text" required value={form.name} onChange={update("name")} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="home-email" className={labelClass}>Email</label>
                  <input id="home-email" data-testid="home-form-email" type="email" required value={form.email} onChange={update("email")} className={inputClass} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-8 mt-8">
                <div>
                  <label htmlFor="home-phone" className={labelClass}>Phone Number (with country code)</label>
                  <input id="home-phone" data-testid="home-form-phone" type="tel" required placeholder="e.g. +1 876 318 4376" value={form.phone} onChange={update("phone")} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="home-country" className={labelClass}>Current Country</label>
                  <select id="home-country" data-testid="home-form-country" required value={form.country} onChange={update("country")} className={inputClass}>
                    <option value="">Select your country</option>
                    {COUNTRIES.map((country) => (
                      <option key={country}>{country}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-8">
                <label htmlFor="home-message" className={labelClass}>Briefly describe your legal matter</label>
                <textarea
                  id="home-message"
                  data-testid="home-form-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Please provide a short overview. Avoid including highly confidential information at this stage."
                  className={`${inputClass} resize-y`}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-6">
                Submitting this form does not create an attorney-client relationship.
              </p>
              <button
                type="submit"
                disabled={sending}
                data-testid="home-form-submit-button"
                className="group w-full mt-6 inline-flex items-center justify-center gap-3 rounded-full bg-navy text-cream text-sm font-bold px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
              >
                {sending ? "Sending…" : "Request Consultation"}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default ConsultationSection;
